import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ImageStrip from './ImageStrip';

const FilamentGroupCard = ({ group, onEdit, onViewHistory, onMarkDepleted }) => {
  const total = Number(group?.totalOriginalWeight || 0);
  const current = Number(group?.totalCurrentWeight || 0);
  const pct = total > 0 ? Math.max(0, Math.min(100, (current / total) * 100)) : 0;
  const colorHex = (group?.colorHex || group?.color_hex || '#A0A0A0').toString();
  const first = group?.filaments?.[0];
  const count = group?.filaments?.length || 0;
  const [expanded, setExpanded] = useState(false);

  const diameterStr = group?.diameter ? `${group?.diameter}mm` : null;
  const levelLabel = pct <= 20 ? 'Estoque baixo' : pct <= 40 ? 'Estoque médio' : 'Estoque bom';

  const groupImages = useMemo(() => {
    const base = Array.isArray(group?.image_urls) ? group?.image_urls : (group?.image ? [group?.image] : []);
    return Array.isArray(base) ? base.slice(0, 4) : [];
  }, [group]);

  // Calcular metragem aproximada usando o primeiro rolo do grupo
  const computeGramatura = (density, diameterMm) => {
    const d = parseFloat(density);
    const dia = parseFloat(diameterMm);
    if (!Number.isFinite(d) || !Number.isFinite(dia) || d <= 0 || dia <= 0) return undefined;
    const radiusCm = (dia / 10) / 2;
    const areaCm2 = Math.PI * Math.pow(radiusCm, 2);
    const volumeCm3PerMeter = areaCm2 * 100;
    const gramsPerMeter = d * volumeCm3PerMeter;
    return gramsPerMeter;
  };

  const gramsPerMeter = (() => {
    const f = group?.filaments?.[0];
    if (!f) return undefined;
    const gpm = parseFloat(f?.gramsPerMeter ?? f?.grams_per_meter ?? '');
    if (Number.isFinite(gpm) && gpm > 0) return gpm;
    const density = f?.density ?? 1.24;
    const dia = parseFloat(f?.diameter || '1.75');
    const computed = computeGramatura(density, dia);
    return Number.isFinite(computed) ? parseFloat(computed.toFixed(3)) : undefined;
  })();
  const remainingMeters = (Number.isFinite(gramsPerMeter) && gramsPerMeter > 0) ? (current / gramsPerMeter) : undefined;
  const rollMeters = (Number.isFinite(gramsPerMeter) && gramsPerMeter > 0) ? (total / gramsPerMeter) : undefined;

  return (
    <section className="group relative rounded-2xl bg-card shadow-none border border-border overflow-hidden transition-all duration-200 hover:shadow-xs">
      {/* Camadas para visual de cards empilhados */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-2 left-2 right-2 bottom-2 rounded-2xl bg-card/70 border border-border"></div>
        <div className="absolute top-4 left-4 right-4 bottom-4 rounded-2xl bg-card/50 border border-border"></div>
      </div>

      {/* Header alinhado com FilamentCard */}
      <header className="pt-5 pl-20 pr-20 pb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="absolute top-4 left-4 w-12 h-12 rounded-full ring-2 ring-border"
            style={{ backgroundColor: colorHex, border: '2px solid var(--color-background)' }}
            title={group?.colorName || 'Cor'}
          />
          {/* Badges à direita (quantidade de rolos + marca) */}
          <div className="absolute top-4 right-4 flex flex-col items-end gap-1">
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20" title={group?.brand}>{group?.brand}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground border border-border/60" title="Quantidade de rolos">{count} rolos</span>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground" title={group?.colorName}>{group?.colorName || 'Indefinida'}</span>
            </div>
            <h3 className="text-base font-medium text-foreground mt-1" title={group?.material || 'Tipo'}>{group?.material || 'Tipo'}</h3>
            {diameterStr && (
              <div className="text-sm text-muted-foreground" title={diameterStr}>{diameterStr}</div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="px-5 pb-5">
        {/* Peso total / Filamento restante */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-muted-foreground">Filamento restante</span>
            <span className="text-sm font-medium text-foreground">
              {current}g{remainingMeters !== undefined ? ` · ${remainingMeters.toFixed(1)} m` : ''}
            </span>
          </div>
          {rollMeters !== undefined && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Grupo (total)</span>
              <span className="text-xs text-foreground">{total}g · {rollMeters.toFixed(1)} m</span>
            </div>
          )}
        </div>

        {/* Progress */}
        <div>
          <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all ${pct <= 20 ? 'bg-error' : pct <= 40 ? 'bg-warning' : 'bg-success'}`}
              style={{ width: `${Math.max(pct, 5)}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className={`text-xs font-medium ${pct <= 20 ? 'text-error' : pct <= 40 ? 'text-warning' : 'text-success'}`}>{levelLabel}</span>
            <span className="text-xs text-muted-foreground">{pct.toFixed(1)}%</span>
          </div>
          {/* Preço médio abaixo da barra */}
          <div className="flex items-center justify-between mt-3">
            <div className="text-sm text-muted-foreground">Preço médio</div>
            <div className="text-right">
              <div className="text-primary text-base font-semibold">R$ {Number(group?.weightedAvgCostPerGram || 0).toFixed(2)}/g</div>
              <div className="text-xs text-muted-foreground">R$ {((Number(group?.weightedAvgCostPerGram || 0)) * 1000).toFixed(2)}/kg</div>
            </div>
          </div>
          {/* Imagens do rolo / amostras do grupo */}
          <ImageStrip images={groupImages} className="mt-4" />
        </div>

        {/* Subcards empilhados: identidade dos cards individuais dentro do agrupamento */}
        {Array.isArray(group?.filaments) && group?.filaments?.length > 0 && (
          <div className="mt-6">
            <p className="text-xs text-muted-foreground mb-2">Rolos no grupo</p>
            {!expanded && (
              <div className="relative h-32">
                {group?.filaments?.slice(0, 3)?.map((f, idx) => {
                  const hex = (f?.colorHex || f?.color_hex || '#A0A0A0').toString();
                  const orig = Number(f?.originalWeight || 0);
                  const curr = Number(f?.currentWeight || 0);
                  const pctRoll = orig > 0 ? Math.max(0, Math.min(100, (curr / orig) * 100)) : 0;
                  const pctCls = pctRoll <= 20 ? 'bg-error' : pctRoll <= 40 ? 'bg-warning' : 'bg-success';
                  const purchaseLabel = f?.purchaseDate || (f?.createdAt ? new Date(f?.createdAt)?.toLocaleDateString('pt-BR') : '-');
                  const offset = idx * 8;
                  return (
                    <div key={f?.id || idx} className="absolute w-[calc(100%-16px)] rounded-xl border border-border bg-card p-2 pl-12 shadow-md" style={{ top: `${offset}px`, left: `${offset}px` }}>
                      <div className="absolute top-2 left-2 w-8 h-8 rounded-full ring-2 ring-border" style={{ backgroundColor: hex }} />
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-foreground truncate" title={`${f?.brand || ''} ${f?.material || ''} ${f?.diameter ? `${f?.diameter}mm` : ''}`}>{f?.brand} {f?.material}{f?.diameter ? ` ${f?.diameter}mm` : ''}</div>
                          <div className="text-xs text-muted-foreground truncate" title={`${f?.colorName || 'Cor'}`}>{f?.colorName || 'Cor'}</div>
                          <div className="text-[10px] text-muted-foreground truncate">Compra: {purchaseLabel}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">{curr}g / {orig}g</div>
                        </div>
                      </div>
                      <div className="mt-1">
                        <div className="w-full h-1 rounded-full bg-muted overflow-hidden">
                          <div className={`h-1 rounded-full ${pctCls}`} style={{ width: `${Math.max(pctRoll, 5)}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                {count > 3 && (
                  <div className="absolute bottom-2 left-2">
                    <Button variant="outline" size="xs" onClick={() => setExpanded(true)}>
                      +{count - 3} rolos
                    </Button>
                  </div>
                )}
              </div>
            )}
            {expanded && (
              <div className="space-y-2 pr-1">
                {group?.filaments?.map((f, idx) => {
                  const hex = (f?.colorHex || f?.color_hex || '#A0A0A0').toString();
                  const orig = Number(f?.originalWeight || 0);
                  const curr = Number(f?.currentWeight || 0);
                  const pctRoll = orig > 0 ? Math.max(0, Math.min(100, (curr / orig) * 100)) : 0;
                  const pctCls = pctRoll <= 20 ? 'bg-error' : pctRoll <= 40 ? 'bg-warning' : 'bg-success';
                  const purchaseLabel = f?.purchaseDate || (f?.createdAt ? new Date(f?.createdAt)?.toLocaleDateString('pt-BR') : '-');
                  return (
                    <div key={f?.id || idx} className="relative rounded-xl border border-border bg-card p-2 pl-12">
                      <div className="absolute top-2 left-2 w-8 h-8 rounded-full ring-2 ring-border" style={{ backgroundColor: hex }} />
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-foreground truncate" title={`${f?.brand || ''} ${f?.material || ''} ${f?.diameter ? `${f?.diameter}mm` : ''}`}>{f?.brand} {f?.material}{f?.diameter ? ` ${f?.diameter}mm` : ''}</div>
                          <div className="text-xs text-muted-foreground truncate" title={`${f?.colorName || 'Cor'}`}>{f?.colorName || 'Cor'}</div>
                          <div className="text-[10px] text-muted-foreground truncate">Compra: {purchaseLabel}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">{curr}g / {orig}g</div>
                        </div>
                      </div>
                      <div className="mt-1">
                        <div className="w-full h-1 rounded-full bg-muted overflow-hidden">
                          <div className={`h-1 rounded-full ${pctCls}`} style={{ width: `${Math.max(pctRoll, 5)}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="flex justify-end">
                  <Button variant="ghost" size="xs" onClick={() => setExpanded(false)}>Fechar</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Rodapé: toolbar contínua com separadores sutis, seguindo identidade dos cards (menor) */}
      <div className="px-5 pb-5">
        <div className="flex rounded-lg overflow-hidden divide-x divide-border border border-border/70 bg-muted/30">
          <Button variant="ghost" size="xs" iconSize={16} className="flex-1 rounded-none" onClick={() => first && onEdit?.(first)} iconName="Edit" iconPosition="left">Editar</Button>
          <Button variant="ghost" size="xs" iconSize={16} className="flex-1 rounded-none" onClick={() => first && onViewHistory?.(first)} iconName="History" iconPosition="left">Histórico</Button>
          <Button variant="ghost" size="xs" iconSize={16} className="flex-1 rounded-none text-error" onClick={() => first && onMarkDepleted?.(first)} iconName="Trash2" iconPosition="left">Excluir</Button>
        </div>
      </div>
    </section>
  );
};

export default FilamentGroupCard;